import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAdmin } from '@/contexts/AdminContext';
import { useLanguage } from '@/contexts/LanguageContext';
import UploadModal from '@/pages/issues/components/UploadModal';

interface Profile {
  id: string;
  display_name: string | null;
  email: string | null;
  gender: string | null;
  source: string | null;
  nationality: string | null;
  birth: string | null;
  created_at: string;
  is_admin: boolean;
}

interface PostStat {
  post_id: number;
  title_en: string;
  title_ko: string;
  category: string;
  total_views: number;
  admin_views: number;
  non_admin_views: number;
  unique_non_admin_viewers: number;
}

interface OverallStats {
  total_posts_with_views: number;
  total_admin_clicks: number;
  total_non_admin_clicks: number;
  unique_non_admin_users: number;
}

const genderMap: Record<string, { en: string; ko: string }> = {
  male: { en: 'Male', ko: '남성' },
  female: { en: 'Female', ko: '여성' },
  'non-binary': { en: 'Non-binary', ko: '논바이너리' },
  'prefer-not-to-say': { en: 'Prefer not to say', ko: '밝히고 싶지 않음' },
};

const AdminUsersPage = () => {
  const { isAdmin, isLoading: authLoading } = useAdmin();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'users' | 'stats'>('users');
  const [postStats, setPostStats] = useState<PostStat[]>([]);
  const [overallStats, setOverallStats] = useState<OverallStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSearch, setStatsSearch] = useState('');
  const [statsCategoryFilter, setStatsCategoryFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchProfiles = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setProfiles(data as Profile[]);
      }
      setLoading(false);
    };
    fetchProfiles();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'stats') return;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-post-stats', {
          method: 'GET',
        });
        if (error) throw new Error(error.message);
        setPostStats(data?.post_stats || []);
        setOverallStats(data?.overall || null);
      } catch (err: any) {
        console.error('Stats fetch error:', err);
        setPostStats([]);
        setOverallStats(null);
      }
      setStatsLoading(false);
    };
    fetchStats();
  }, [isAdmin, activeTab]);

  const filtered = profiles.filter((p) => {
    const q = search.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (p.display_name?.toLowerCase().includes(q) ?? false) ||
      (p.email?.toLowerCase().includes(q) ?? false) ||
      (p.source?.toLowerCase().includes(q) ?? false) ||
      (p.nationality?.toLowerCase().includes(q) ?? false);

    const matchesGender = genderFilter === 'all' || p.gender === genderFilter;

    return matchesSearch && matchesGender;
  });

  // Filtered post stats
  const filteredPostStats = postStats.filter((stat) => {
    const q = statsSearch.toLowerCase().trim();
    const matchesSearch =
      !q ||
      stat.title_en?.toLowerCase().includes(q) ||
      stat.title_ko?.toLowerCase().includes(q) ||
      stat.category?.toLowerCase().includes(q);
    const matchesCategory =
      statsCategoryFilter === 'all' || stat.category === statsCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Unique categories from postStats
  const statCategories = Array.from(
    new Set(postStats.map((s) => s.category).filter(Boolean))
  ).sort();

  // Delete handler
  const handleDeletePost = async (postId: number) => {
    const ok = window.confirm(
      lang === 'ko'
        ? '이 게시물을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
        : 'Delete this post? This action cannot be undone.'
    );
    if (!ok) return;

    try {
      // Delete post_views first (FK-ish)
      await supabase.from('post_views').delete().eq('post_id', postId);
      // Delete post
      const { error } = await supabase.from('posts').delete().eq('id', postId);
      if (error) throw error;

      // Update local state
      setPostStats((prev) => prev.filter((s) => s.post_id !== postId));

      // Update overall stats
      if (overallStats) {
        const removedStat = postStats.find((s) => s.post_id === postId);
        if (removedStat) {
          const newAdmin = overallStats.total_admin_clicks - removedStat.admin_views;
          const newNonAdmin = overallStats.total_non_admin_clicks - removedStat.non_admin_views;
          const newPostsCount = new Set(
            postStats.filter((s) => s.post_id !== postId).map((s) => s.post_id)
          ).size;
          setOverallStats({
            ...overallStats,
            total_posts_with_views: newPostsCount,
            total_admin_clicks: newAdmin,
            total_non_admin_clicks: newNonAdmin,
          });
        }
      }
    } catch (err: any) {
      console.error('Delete post error:', err);
      alert(
        lang === 'ko'
          ? '게시물 삭제에 실패했습니다. 다시 시도해주세요.'
          : 'Failed to delete post. Please try again.'
      );
    }
  };

  if (authLoading || (!authLoading && !isAdmin)) return null;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 py-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 mb-1">
              {activeTab === 'users'
                ? t('User Management', '사용자 관리')
                : t('Post View Statistics', '게시물 조회수 통계')}
            </h1>
            <p className="text-sm text-stone-500">
              {activeTab === 'users'
                ? `${profiles.length} ${lang === 'ko' ? '명의 가입자' : 'registered users'}`
                : `${lang === 'ko' ? '관리자/비관리자 조회수 구분 통계' : 'Admin vs non-admin view counts'}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 text-xs px-5 py-2.5 bg-stone-900 text-white rounded-lg hover:bg-stone-700 cursor-pointer transition-all whitespace-nowrap font-semibold"
            >
              <i className="ri-add-line text-sm" />
              {t('New Post', '새 게시물')}
            </button>
            <span className="text-[10px] px-3 py-1 bg-stone-800 text-white rounded-full font-semibold whitespace-nowrap">
              <i className="ri-shield-check-line mr-1" />
              {t('Admin', '관리자')}
            </span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center border border-stone-200 rounded-full p-1 w-fit mb-6 bg-white">
          <button
            onClick={() => setActiveTab('users')}
            className={`text-xs px-5 py-2 rounded-full cursor-pointer transition-all whitespace-nowrap font-semibold ${
              activeTab === 'users'
                ? 'bg-stone-900 text-white'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <i className="ri-user-line mr-1.5" />
            {t('Users', '사용자')}
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`text-xs px-5 py-2 rounded-full cursor-pointer transition-all whitespace-nowrap font-semibold ${
              activeTab === 'stats'
                ? 'bg-stone-900 text-white'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            <i className="ri-bar-chart-line mr-1.5" />
            {t('Post Stats', '조회수 통계')}
          </button>
        </div>

        {/* === USERS TAB === */}
        {activeTab === 'users' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('Search by name, email, source, or nationality...', '이름, 이메일, 알게된 경로, 국적으로 검색...')}
                  className="w-full text-sm pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                />
              </div>
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400 bg-white text-stone-600 cursor-pointer"
              >
                <option value="all">{t('All Genders', '모든 성별')}</option>
                <option value="male">{lang === 'ko' ? '남성' : 'Male'}</option>
                <option value="female">{lang === 'ko' ? '여성' : 'Female'}</option>
                <option value="non-binary">{lang === 'ko' ? '논바이너리' : 'Non-binary'}</option>
                <option value="prefer-not-to-say">{lang === 'ko' ? '밝히고 싶지 않음' : 'Prefer not to say'}</option>
              </select>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              {loading ? (
                <div className="py-20 flex items-center justify-center text-stone-400 text-sm">
                  <i className="ri-loader-4-line animate-spin mr-2 text-base" />
                  {t('Loading...', '불러오는 중...')}
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-20 text-center text-sm text-stone-400">
                  <i className="ri-user-search-line text-2xl mb-2 block" />
                  {t('No users found.', '사용자를 찾을 수 없습니다.')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 bg-stone-50">
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Name', '이름')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Email', '이메일')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Gender', '성별')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Source', '알게된 경로')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Nationality', '국적')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Birth', '생년월일')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Joined', '가입일')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((profile) => (
                        <tr key={profile.id} className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap font-medium text-stone-800">
                            {profile.display_name || <span className="text-stone-400 italic">{t('No name', '이름 없음')}</span>}
                            {profile.is_admin && (
                              <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-stone-800 text-white rounded font-semibold">{t('Admin', '관리자')}</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-600">{profile.email || <span className="text-stone-300 italic">—</span>}</td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-600">
                            {profile.gender ? (genderMap[profile.gender]?.[lang] || profile.gender) : <span className="text-stone-300 italic">—</span>}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-600 max-w-[160px] truncate" title={profile.source || ''}>
                            {profile.source || <span className="text-stone-300 italic">—</span>}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-600">{profile.nationality || <span className="text-stone-300 italic">—</span>}</td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-600">{profile.birth || <span className="text-stone-300 italic">—</span>}</td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-stone-500 text-xs">
                            {new Date(profile.created_at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* === STATS TAB === */}
        {activeTab === 'stats' && (
          <>
            {/* Overall Stats Cards */}
            {overallStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg">
                      <i className="ri-eye-line text-stone-600 text-sm" />
                    </div>
                    <span className="text-xs font-semibold text-stone-500">{lang === 'ko' ? '전체 조회' : 'Total Views'}</span>
                  </div>
                  <p className="text-2xl font-black text-stone-800">{overallStats.total_admin_clicks + overallStats.total_non_admin_clicks}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
                      <i className="ri-shield-star-line text-amber-600 text-sm" />
                    </div>
                    <span className="text-xs font-semibold text-stone-500">{lang === 'ko' ? '관리자 클릭' : 'Admin Clicks'}</span>
                  </div>
                  <p className="text-2xl font-black text-amber-600">{overallStats.total_admin_clicks}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                      <i className="ri-user-heart-line text-teal-600 text-sm" />
                    </div>
                    <span className="text-xs font-semibold text-stone-500">{lang === 'ko' ? '일반 사용자' : 'User Clicks'}</span>
                  </div>
                  <p className="text-2xl font-black text-teal-600">{overallStats.total_non_admin_clicks}</p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-rose-50 rounded-lg">
                      <i className="ri-file-list-line text-rose-600 text-sm" />
                    </div>
                    <span className="text-xs font-semibold text-stone-500">{lang === 'ko' ? '조회된 게시물' : 'Posts with Views'}</span>
                  </div>
                  <p className="text-2xl font-black text-rose-600">{overallStats.total_posts_with_views}</p>
                </div>
              </div>
            )}

            {/* Stats Filters */}
            <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="text"
                  value={statsSearch}
                  onChange={(e) => setStatsSearch(e.target.value)}
                  placeholder={t('Search by post title or category...', '게시물 제목이나 카테고리로 검색...')}
                  className="w-full text-sm pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                />
              </div>
              <select
                value={statsCategoryFilter}
                onChange={(e) => setStatsCategoryFilter(e.target.value)}
                className="text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400 bg-white text-stone-600 cursor-pointer"
              >
                <option value="all">{lang === 'ko' ? '모든 카테고리' : 'All Categories'}</option>
                {statCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Per-Post Stats Table */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              {statsLoading ? (
                <div className="py-20 flex items-center justify-center text-stone-400 text-sm">
                  <i className="ri-loader-4-line animate-spin mr-2 text-base" />
                  {t('Loading statistics...', '통계 불러오는 중...')}
                </div>
              ) : filteredPostStats.length === 0 ? (
                <div className="py-20 text-center text-sm text-stone-400">
                  <i className="ri-bar-chart-box-line text-2xl mb-2 block" />
                  {statsSearch || statsCategoryFilter !== 'all'
                    ? lang === 'ko'
                      ? '검색 결과가 없습니다.'
                      : 'No results found.'
                    : lang === 'ko'
                    ? '아직 조회 데이터가 없습니다. 게시물을 클릭하면 통계가 쌓입니다.'
                    : 'No view data yet. Click on posts to collect statistics.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 bg-stone-50">
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Post', '게시물')}</th>
                        <th className="text-left px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Category', '카테고리')}</th>
                        <th className="text-center px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{lang === 'ko' ? '총 조회' : 'Total'}</th>
                        <th className="text-center px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{lang === 'ko' ? '관리자' : 'Admin'}</th>
                        <th className="text-center px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{lang === 'ko' ? '일반 사용자' : 'Users'}</th>
                        <th className="text-center px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{lang === 'ko' ? '고유 사용자' : 'Unique Users'}</th>
                        <th className="text-center px-5 py-3 font-semibold text-stone-500 whitespace-nowrap">{t('Actions', '관리')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPostStats.map((stat) => (
                        <tr key={stat.post_id} className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap font-medium text-stone-800">
                            {lang === 'ko' ? stat.title_ko : stat.title_en}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <span className="text-[10px] font-semibold px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full">
                              {stat.category}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center font-bold text-stone-800">
                            {stat.total_views}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <span className="text-amber-600 font-semibold">{stat.admin_views}</span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <span className="text-teal-600 font-semibold">{stat.non_admin_views}</span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <span className="text-stone-600 font-semibold">{stat.unique_non_admin_viewers}</span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <button
                              onClick={() => handleDeletePost(stat.post_id)}
                              className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-colors"
                              title={lang === 'ko' ? '삭제' : 'Delete'}
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)} />
      )}
    </div>
  );
};

export default AdminUsersPage;